import { useInsertionEffect, useState } from "react"

function handleSrc(src : string | undefined) {
	if (!src || src.startsWith("/public/")) return null

	return src
}

function createElementWithAttributes(
	tagName: string,
	attributes: { [k: string]: string }
) {
	const el = document.createElement(tagName)

	for (const [attrName, attrValue] of Object.entries(attributes)) {
		el.setAttribute(attrName, attrValue)
	}

	return el
}

/**
 * Представляет тэги, которые располагаются внутри `head` в json-формате.
 */
export interface HtmlHeadTags {
	/** [`<title />`](https://developer.mozilla.org/en-US/docs/Web/HTML/Element/title) */
	title?: string
	/** [`<link />`](https://developer.mozilla.org/en-US/docs/Web/HTML/Element/link) */
	links?: { [k: string]: any }[]
	/** [`<meta />`](https://developer.mozilla.org/en-US/docs/Web/HTML/Element/meta) */
	meta?: { [k: string]: any }[]
	/** [`<script />`](https://developer.mozilla.org/en-US/docs/Web/HTML/Element/script) */
	scripts?: { [k: string]: any }[]
}

function addHeadTags({
						 title,
						 links,
						 meta,
						 scripts
					 }: HtmlHeadTags) {
	if (typeof title !== "undefined" && title !== null) {
		document.title = title
	}

	const elements: any[] = []

	if (meta) {
		for (const attrs of meta) {
			elements.push(createElementWithAttributes("meta", attrs))
		}
	}

	const loadingLinksPromises: Promise<unknown>[] = []

	if (links) {
		for (const attrs of links) {
			attrs.href = handleSrc(attrs.href)

			if (attrs.href) {

				let res, rej

				const promise = new Promise((resolve, reject) => {
					res = resolve
					rej = reject
				})

				loadingLinksPromises.push(promise)

				const el = createElementWithAttributes("link", attrs)

				el.onload = () => res && res()
				el.onerror = () => rej && rej()

				elements.push(el)
			}
		}
	}

	const loadingScriptPromises: Promise<unknown>[] = []

	if (scripts) {
		for (const attrs of scripts) {
			attrs.src = handleSrc(attrs.src)

			if (attrs.src) {
				const el = createElementWithAttributes("script", attrs)

				let res, rej

				const promise = new Promise((resolve, reject) => {
					res = resolve
					rej = reject
				})

				loadingScriptPromises.push(promise)

				el.onload = () => res && res()
				el.onerror = () => rej && rej()

				elements.push(el)
			}
		}
	}

	if (elements.length > 0) {
		document.head.append(...elements)
	}

	return {
		elements,
		loadingScriptsPromise: Promise.all(loadingScriptPromises),
		loadingLinksPromises: Promise.all(loadingLinksPromises)
	}
}

/**
 * Добавляет в `head` элементы и удаляет их при размонтировании компонента
 * @param htmlHeadTags
 * @return состояние загрузки script'ов и link`ов
 * @hook
 * @category Hook
 * @example
 * ```tsx
 * import React, {useEffect} from "react";
 * import {useHtmlHead} from "~/shared/hooks";
 *
 * function Example() {
 *
 *     const { scriptsLoaded } = useHtmlHead({
 *         title: "Заголовок",
 *         scripts: [
 *             {
 *                 src: "https://code.jquery.com/jquery-3.6.3.min.js",
 *                 integrity: "sha256-pvPw+upLPUjgMXY0G+8O0xUf+/Im1MZjXxxgOcBQBXU=",
 *                 crossorigin: "anonymous"
 *             }
 *         ]
 *     })
 *
 *     useEffect(() => {
 *
 *         if(!scriptsLoaded) return;
 *
 *         const mydiv = jQuery("#mydiv"); // eslint-disable-line
 *         mydiv.text("Обновленный тестовый компонент");
 *     }, [scriptsLoaded])
 *
 *     return scriptsLoaded ? <div id="mydiv">Тестовый компонент</div> : null;
 * }
 * ```
 */
export function useHtmlHead({
								title,
								links,
								meta,
								scripts
							}: HtmlHeadTags) {
	const [scriptsLoaded, setScriptsLoaded] = useState(false)
	const [linksLoaded, setLinksLoaded] = useState(false)

	useInsertionEffect(() => {
		const { elements, loadingScriptsPromise, loadingLinksPromises } = addHeadTags({
			title,
			links,
			meta,
			scripts
		})

		let unmounted = false

		loadingScriptsPromise
			.then(() => !unmounted && setScriptsLoaded(true))
			.catch((err) => {
				console.error(err)

				if (!unmounted) {
					setScriptsLoaded(true)
				}
			})

		loadingLinksPromises
			.then(() => !unmounted && setLinksLoaded(true))
			.catch((err) => {
				console.error(err)

				if (!unmounted) {
					setLinksLoaded(true)
				}
			})

		return () => {
			unmounted = true

			for (const el of elements) {
				el.remove()
			}
		}
	}, [title, links, meta, scripts])

	return { scriptsLoaded, linksLoaded }
}
